/**
 * ErrorBoundary Component
 *
 * A React error boundary that catches JavaScript errors anywhere in its child
 * component tree and displays a fallback UI instead of crashing the whole app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 */
import React, { Component, ErrorInfo, ReactNode } from "react";

// import * as Sentry from "@sentry/react"; // Remove direct Sentry import
import { logError } from "../../services/errorReporting"; // Import centralized logger

interface ErrorBoundaryProps {
  /** The child components wrapped by the error boundary */
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error occurs
   * This lifecycle is invoked after an error has been thrown by a descendant component
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state to display fallback UI
    return { hasError: true, error };
  }

  /**
   * Catch errors and get detailed error information
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error using the centralized service
    logError(error, { componentStack: errorInfo.componentStack });

    // Log the error to console (logError already does this, but keeping for potential redundancy)
    // console.error("Uncaught error:", error, errorInfo);

    // Capture and send to Sentry if it's available - Handled by logError now
    /*
    if (Sentry && typeof Sentry.captureException === "function") {
      Sentry.captureException(error, {
        extra: {
          ...errorInfo,
        },
      });
    }
    */

    this.setState({ errorInfo }); // Keep storing errorInfo for potential display
  }

  /**
   * Try to recover from the error
   */
  handleResetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    // If no error, render children normally
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Error fallback UI
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              We&apos;ve encountered an unexpected error. Our team has been notified
              and is working to resolve the issue. Please try refreshing the page
            </p>

            {process.env.NODE_ENV === "development" && (
              <div className="mb-6 text-left">
                <div className="bg-gray-100 p-4 rounded text-sm font-mono text-red-600 overflow-auto max-h-64">
                  <p>{this.state.error && this.state.error.toString()}</p>
                  <p className="mt-2 text-gray-700">
                    {this.state.errorInfo &&
                      this.state.errorInfo.componentStack}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => (window.location.href = "/")}
                className="btn btn-secondary"
              >
                Go to Homepage
              </button>
              <button
                onClick={this.handleResetError}
                className="btn btn-primary"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary; 
